// src/store/useAppStore.ts

import axios, { type AxiosResponse } from "axios";
import { produce } from "immer";
import { debounce } from "lodash";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { notify } from "@/components/Notification";
import { MAX_DOCUMENT_SIZE_BYTES, MAX_DOCUMENT_SIZE_MB } from "@/lib/constants";
import type {
  AdditionalGenerationOptions,
  AdditionalGenerationState,
  CostBreakdown,
  DocumentInfo,
  DocumentOptionKey,
  FastAPIJobStatusUpdate,
  FastApiJobSummaryResponse,
  FileData,
  JobStatus,
  LogEvent,
  ProcessDocumentApiResponse,
  ProcessingJob,
  PromoCodeResult,
  StageDefinition,
  StatusTransition,
  UserInfo,
} from "@/types";
import {
  applyPromoCode as apiApplyPromoCode,
  calculateCreditCost,
  handleDownload,
  safeStringify,
} from "@/utils/api";
import { calculateCostBreakdown } from "@/utils/documentUtils";
import { trackEvent } from "@/utils/gtm";

// Guard to disable persist if storage is not writable
let __persistDisabled = false;
const __canWriteStorage = () => {
  try {
    localStorage.setItem("__app_storage_test__", "1");
    localStorage.removeItem("__app_storage_test__");
    return true;
  } catch (_) {
    return false;
  }
};

const DEFAULT_COST_BREAKDOWN: CostBreakdown = {
  baseCost: 0,
  multipleChoiceCost: 0,
  shortAnswerCost: 0,
  ankiFlashcardsCost: 0,
  presentationSlidesCost: 0,
  summaryCost: 0,
  mindmapCost: 0,
  readabilityAnalysisCost: 0,
  lessonPlanCost: 0,
  quizDiscount: 0,
  totalCost: 0,
} as const;

const updateJobStatus = (responseStatus: any): JobStatus => ({
  id: "", // Required field
  job_id: "", // Required field
  request_id: "", // Required field
  status: responseStatus.status,
  progress_percentage: responseStatus.progress_percentage,
  status_description: responseStatus.status_description,
  error_message: null,
  last_processed: null,
  last_optimized: null,
  optimization_quality_score: null,
  total_input_tokens: null,
  total_output_tokens: null,
  created_at: new Date(),
  updated_at: new Date(),
  last_polled: new Date(),
});

interface AppState {
  session: any;
  documentFile: File | null;
  generateMultipleChoiceQuiz: boolean;
  generateShortAnswerQuiz: boolean;
  generateAnkiFlashcards: boolean;
  generatePresentationSlides: boolean;
  generateSummary: boolean;
  generateMindmap: boolean;
  generateReadabilityAnalysis: boolean;
  generateLessonPlan: boolean;
  lessonPlanTargetAudience: string;
  presentationTheme: string;
  documentTitle: string;
  jobStatus: JobStatus | null;
  showPreview: boolean;
  showPastJobs: boolean;
  showProgressTracker: boolean;
  jobCompleted: boolean;
  showLogViewer: boolean;
  currentJobId: string | null;
  currentRequestId: string | null;
  logViewerKey: string | null;
  documentSize: number | null;
  creditCost: number | null;
  showConfirmModal: boolean;
  promoCode: string;
  costBreakdown: CostBreakdown;
  documentInfo: DocumentInfo | null;
  userInfo: UserInfo | null;
  isProcessingDocument: boolean;
  isApplyingPromoCode: boolean;
  isLoadingUserInfo: boolean;
  jobCompletionHandled: boolean;
  isCheckingStatus: boolean;
  isLoadingDocumentInfo: boolean;
  zipDownloaded: boolean;
  logs: { [job_id: string]: LogEvent[] };
  error: string | null;
  resultsReady: boolean;
  processedFiles: FileData[];
  hasValidJobId: boolean;
  lastKnownStatus: string | null;
  lastUpdateTime: string | null;
  lastLogId: number | null;
  pastJobs: ProcessingJob[];
  isDownloading: boolean;
  isLoadingFiles: boolean;
  scrollToResults: boolean;
  appliedPromoCode: PromoCodeResult | null;
  lastStatusCheck: number;
  statusCheckAttempts: number;
  additionalGeneration: AdditionalGenerationState;
}

interface AppActions {
  setSession: (session: any) => void;
  setDocumentFile: (file: File | null) => void;
  setGenerateMultipleChoiceQuiz: (generate: boolean) => void;
  setGenerateShortAnswerQuiz: (generate: boolean) => void;
  setGenerateAnkiFlashcards: (generate: boolean) => void;
  setGeneratePresentationSlides: (generate: boolean) => void;
  setGenerateSummary: (generate: boolean) => void;
  setGenerateMindmap: (generate: boolean) => void;
  setGenerateReadabilityAnalysis: (generate: boolean) => void;
  setGenerateLessonPlan: (generate: boolean) => void;
  setLessonPlanTargetAudience: (audience: string) => void;
  setPresentationTheme: (theme: string) => void;
  setShowPastJobs: (show: boolean) => void;
  setShowConfirmModal: (show: boolean) => void;
  setJobStatus: (status: JobStatus) => void;
  setCurrentJobId: (job_id: string | null) => void;
  setCurrentRequestId: (request_id: string | null) => void;
  handleDocumentChange: (file: File | null) => void;
  debouncedHandleDocumentChange: (file: File | null) => void;
  handleOptionChange: (option: string, checked: boolean) => void;
  resetDocumentState: () => void;
  handleSubmit: (
    submitFile: File,
    submitOptions: {
      generateMultipleChoiceQuiz: boolean;
      generateShortAnswerQuiz: boolean;
      generateAnkiFlashcards: boolean;
      generatePresentationSlides: boolean;
      generateSummary: boolean;
      generateMindmap: boolean;
      generateReadabilityAnalysis: boolean;
      generateLessonPlan: boolean;
      lessonPlanTargetAudience: string;
      presentationTheme: string;
    },
  ) => void;
  confirmProcessing: () => Promise<void>;
  handleJobComplete: (job_id: string) => Promise<void>;
  recalculateCosts: (
    file: File,
    options: {
      generateMultipleChoiceQuiz: boolean;
      generateShortAnswerQuiz: boolean;
      generateAnkiFlashcards: boolean;
      generatePresentationSlides: boolean;
      generateSummary: boolean;
      generateMindmap: boolean;
      generateReadabilityAnalysis: boolean;
      generateLessonPlan: boolean;
    },
  ) => Promise<void>;
  refetchUserInfo: () => Promise<void>;
  handlePreview: () => void;
  setIsProcessingDocument: (isLoading: boolean) => void;
  setIsApplyingPromoCode: (isLoading: boolean) => void;
  applyPromoCodeWrapper: (code: string) => Promise<PromoCodeResult | null>;
  setError: (error: string | null) => void;
  setShowProgressTracker: (show: boolean) => void;
  setShowLogViewer: (show: boolean) => void;
  fetchProcessedFiles: (job_id: string) => Promise<void>;
  resetJobState: () => void;
  setHasValidJobId: (value: boolean) => void;
  setLogViewerKey: (key: string) => void;
  updateJobStatus: (status: FastAPIJobStatusUpdate) => void;
  addLogEntries: (job_id: string, logs: LogEvent[]) => void;
  fetchJobStatus: () => Promise<void>;
  forceRefreshJobStatus: () => Promise<void>;
  fetchLogs: (requestId: string, lastLogId: number | null) => Promise<number | null>;
  setProcessedFiles: (files: FileData[]) => void;
  setLastKnownStatus: (status: string) => void;
  setLastUpdateTime: (time: string) => void;
  setScrollToResults: (scroll: boolean) => void;
  clearPromoCode: () => void;
  startAdditionalGeneration: (jobId: string, options: AdditionalGenerationOptions) => Promise<void>;
  pollAdditionalGenerationStatus: () => Promise<void>;
  handleAdditionalGenerationComplete: (jobId: string) => Promise<void>;
  updateGenerationFlags: (flags: {
    requested: Record<string, boolean>;
    completed: Record<string, boolean>;
  }) => void;
  logStatusTransition: (fromStatus: string, toStatus: string) => void;
  updateGenerationStages: (options: AdditionalGenerationOptions) => void;
  normalizeStatus: (status: string) => string;
  handlePollingError: (error: unknown) => void;
  hasProgressInFlags: (
    oldFlags: { completed: Record<string, boolean> } | null,
    newFlags: { completed: Record<string, boolean> },
  ) => boolean;
  generateStatusDescription: (status: string, progress: number) => string;
  forceReset: () => void;
}

export const useAppStoreActions = () => {
  return useAppStore((state) => ({ forceReset: state.forceReset }));
};

const useAppStore = create<AppState & AppActions>()(
  devtools(
    persist(
      (set, get) => ({
        session: null,
        documentFile: null,
        generateMultipleChoiceQuiz: false,
        generateShortAnswerQuiz: false,
        generateAnkiFlashcards: false,
        generatePresentationSlides: false,
        generateSummary: false,
        generateMindmap: false,
        generateReadabilityAnalysis: false,
        generateLessonPlan: false,
        lessonPlanTargetAudience:
          "American high school students between the ages of 15 and 18 with average scholastic aptitude",
        presentationTheme: "black",
        documentTitle: "",
        jobStatus: null,
        showPreview: false,
        showPastJobs: true,
        showProgressTracker: false,
        jobCompleted: false,
        showLogViewer: false,
        currentJobId: null,
        currentRequestId: null,
        logViewerKey: null,
        documentSize: null,
        creditCost: null,
        showConfirmModal: false,
        promoCode: "",
        costBreakdown: {
          baseCost: 0,
          multipleChoiceCost: 0,
          shortAnswerCost: 0,
          ankiFlashcardsCost: 0,
          presentationSlidesCost: 0,
          summaryCost: 0,
          mindmapCost: 0,
          readabilityAnalysisCost: 0,
          lessonPlanCost: 0,
          quizDiscount: 0,
          totalCost: 0,
        },
        documentInfo: null,
        userInfo: null,
        isProcessingDocument: false,
        isApplyingPromoCode: false,
        isLoadingUserInfo: false,
        jobCompletionHandled: false,
        isCheckingStatus: false,
        isLoadingDocumentInfo: false,
        zipDownloaded: false,
        logs: {},
        error: null,
        resultsReady: false,
        processedFiles: [],
        hasValidJobId: false,
        lastKnownStatus: null,
        lastUpdateTime: null,
        lastLogId: null,
        isDownloading: false,
        isLoadingFiles: false,
        pastJobs: [],
        scrollToResults: false,
        appliedPromoCode: null,
        lastStatusCheck: 0,
        statusCheckAttempts: 0,

        setSession: (session) => set({ session }),
        setDocumentFile: (file) => set({ documentFile: file }),
        setGenerateMultipleChoiceQuiz: (generate) => set({ generateMultipleChoiceQuiz: generate }),
        setGenerateShortAnswerQuiz: (generate) => set({ generateShortAnswerQuiz: generate }),
        setGenerateAnkiFlashcards: (generate) => set({ generateAnkiFlashcards: generate }),
        setGeneratePresentationSlides: (generate) => set({ generatePresentationSlides: generate }),
        setGenerateSummary: (generate) => set({ generateSummary: generate }),
        setGenerateMindmap: (generate) => set({ generateMindmap: generate }),
        setGenerateReadabilityAnalysis: (generate) =>
          set({ generateReadabilityAnalysis: generate }),
        setGenerateLessonPlan: (generate) => set({ generateLessonPlan: generate }),
        setLessonPlanTargetAudience: (audience) => set({ lessonPlanTargetAudience: audience }),
        setPresentationTheme: (theme) => set({ presentationTheme: theme }),
        setShowPastJobs: (show) => set({ showPastJobs: show }),
        setShowConfirmModal: (show) => set({ showConfirmModal: show }),
        setIsProcessingDocument: (isLoading) => set({ isProcessingDocument: isLoading }),
        setIsApplyingPromoCode: (isLoading) => set({ isApplyingPromoCode: isLoading }),
        setJobStatus: (status: JobStatus) => set({ jobStatus: status }),
        setCurrentJobId: (job_id) => set({ currentJobId: job_id }),
        setCurrentRequestId: (request_id) => set({ currentRequestId: request_id }),
        setError: (error) => set({ error }),
        setShowProgressTracker: (show: boolean) => set({ showProgressTracker: show }),
        setShowLogViewer: (show: boolean) => set({ showLogViewer: show }),
        setHasValidJobId: (value: boolean) => set({ hasValidJobId: value }),
        setLogViewerKey: (key: string) => set({ logViewerKey: key }),
        setProcessedFiles: (files: FileData[]) => set({ processedFiles: files }),
        setLastKnownStatus: (status: string) => set({ lastKnownStatus: status }),
        setLastUpdateTime: (time: string) => set({ lastUpdateTime: time }),
        setScrollToResults: (scroll: boolean) => set({ scrollToResults: scroll }),
        clearPromoCode: () => set({ promoCode: "", appliedPromoCode: null }),

        resetDocumentState: () => {
          console.log("Resetting document state...");

          // Clear from localStorage to prevent stale data persistence
          if (typeof window !== "undefined") {
            const storage = window.localStorage;
            // Only remove specific keys related to document state
            storage.removeItem("app-storage-documentFile");
            storage.removeItem("app-storage-documentInfo");
            storage.removeItem("app-storage-documentTitle");
            storage.removeItem("app-storage-creditCost");
            storage.removeItem("app-storage-costBreakdown");
          }

          set({
            // Clear file-related state
            documentFile: null,
            documentInfo: null,
            documentTitle: "",
            documentSize: null,

            // Clear processing state
            creditCost: null,
            costBreakdown: {
              baseCost: 0,
              multipleChoiceCost: 0,
              shortAnswerCost: 0,
              ankiFlashcardsCost: 0,
              presentationSlidesCost: 0,
              summaryCost: 0,
              mindmapCost: 0,
              readabilityAnalysisCost: 0,
              lessonPlanCost: 0,
              quizDiscount: 0,
              totalCost: 0,
            },

            // Reset all generation options
            generateMultipleChoiceQuiz: false,
            generateShortAnswerQuiz: false,
            generateAnkiFlashcards: false,
            generatePresentationSlides: false,
            generateSummary: false,
            generateMindmap: false,
            generateReadabilityAnalysis: false,
            generateLessonPlan: false,
            lessonPlanTargetAudience:
              "American high school students between the ages of 15 and 18 with average scholastic aptitude",
            presentationTheme: "black",

            // Clear any error states
            error: null,

            // Reset UI states
            showPreview: false,
            isLoadingDocumentInfo: false,

            // Clear job-related state
            jobStatus: null,
            currentJobId: null,
            currentRequestId: null,
            processedFiles: [],
            isProcessingDocument: false,

            // Clear flags and progress
            jobCompleted: false,
            jobCompletionHandled: false,
            zipDownloaded: false,
            resultsReady: false,
            hasValidJobId: false,

            // Reset tracking states
            lastKnownStatus: null,
            lastUpdateTime: null,
            lastLogId: null,

            // Reset auto-refresh state
            scrollToResults: false,

            // Clear logs
            logs: {},

            // Reset additional generation state
            additionalGeneration: {
              isGeneratingAdditional: false,
              additionalGenerationJobId: null,
              additionalGenerationProgress: 0,
              additionalGenerationStatus: "",
              additionalGenerationError: null,
              existingDocumentTypes: [],
              lastKnownGenerationFlags: null,
              stages: [],
              statusTransitions: [],
              optimization_qualityScore: null,
              inputTokens: null,
              outputTokens: null,
              lastProcessed: null,
              lastOptimized: null,
              statusDescription: "",
              hasSeenProcessingState: false,
              processingStartTime: null,
              showResults: false,
              keepModalOpen: false,
            },
          });

          // Force a garbage collection suggestion to the browser
          if (typeof window !== "undefined" && window.gc) {
            try {
              window.gc();
            } catch (e) {
              console.debug("Manual GC not available");
            }
          }

          console.log("Document state reset complete");
        },

        handleDocumentChange: async (file: File | null) => {
          console.log("handleDocumentChange called with file:", file?.name || "null");

          if (!file) {
            set({
              documentFile: null,
              documentInfo: null,
              documentTitle: "",
              documentSize: null,
              creditCost: null,
              costBreakdown: {
                baseCost: 0,
                multipleChoiceCost: 0,
                shortAnswerCost: 0,
                ankiFlashcardsCost: 0,
                presentationSlidesCost: 0,
                summaryCost: 0,
                mindmapCost: 0,
                readabilityAnalysisCost: 0,
                lessonPlanCost: 0,
                quizDiscount: 0,
                totalCost: 0,
              },
              showPreview: false,
              error: null,
              isLoadingDocumentInfo: false,
              isProcessingDocument: false,
              // If needed, reset other related flags here
            });
            return;
          }

          // Clear existing state and set new file
          get().resetDocumentState();
          set({ documentFile: file });
          get().debouncedHandleDocumentChange(file);
        },

        debouncedHandleDocumentChange: debounce(async (file: File | null) => {
          console.log("debouncedHandleDocumentChange executing for file:", file?.name || "null");

          if (!file) {
            console.log("No file present in debounced handler, returning early");
            return; // Just return since state was already cleared in handleDocumentChange
          }

          set({
            isProcessingDocument: true,
            showPreview: false,
            jobStatus: null,
            showProgressTracker: false,
            showLogViewer: false,
            jobCompleted: false,
          });

          try {
            set({ documentTitle: file.name, documentSize: file.size });

            if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
              throw new Error(
                `File size exceeds the maximum limit of ${MAX_DOCUMENT_SIZE_MB.toFixed(0)} MB`,
              );
            }

            const formData = new FormData();
            formData.append("file", file);
            // Get the current state to access the option values
            const state = get();
            formData.append("generateMultipleChoiceQuiz", String(state.generateMultipleChoiceQuiz));
            formData.append("generateShortAnswerQuiz", String(state.generateShortAnswerQuiz));
            formData.append("generateAnkiFlashcards", String(state.generateAnkiFlashcards));
            formData.append("generatePresentationSlides", String(state.generatePresentationSlides));
            formData.append("generateSummary", String(state.generateSummary));
            formData.append("generateMindmap", String(state.generateMindmap));
            formData.append(
              "generateReadabilityAnalysis",
              String(state.generateReadabilityAnalysis),
            );
            formData.append("generateLessonPlan", String(state.generateLessonPlan));

            const response = await axios.post<DocumentInfo>("/api/get_document_info", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
              timeout: 300000, // 5 minutes
            });

            set(
              produce((state: AppState) => {
                state.documentInfo = response.data;
                state.creditCost = response.data.costBreakdown?.totalCost ?? 0;
                state.costBreakdown = response.data.costBreakdown ?? DEFAULT_COST_BREAKDOWN;
                state.showPreview = true;
              }),
            );
          } catch (error) {
            console.error("Error handling document change:", safeStringify(error));
            set({
              documentTitle: "",
              documentSize: null,
              creditCost: null,
              costBreakdown: {
                baseCost: 0,
                multipleChoiceCost: 0,
                shortAnswerCost: 0,
                ankiFlashcardsCost: 0,
                presentationSlidesCost: 0,
                summaryCost: 0,
                mindmapCost: 0,
                readabilityAnalysisCost: 0,
                lessonPlanCost: 0,
                quizDiscount: 0,
                totalCost: 0,
              },
              showPreview: false,
            });
            if (error instanceof Error) {
              get().setError(error.message);
            } else {
              get().setError("An unexpected error occurred while processing the document");
            }
          } finally {
            set({
              isLoadingDocumentInfo: false,
              isProcessingDocument: false,
            });
          }
        }, 300),

        handleOptionChange: (option: string, checked: boolean) => {
          // Get current state once
          const state = get();
          const file = state.documentFile;

          // Batch the option update
          set(
            produce((draft: AppState) => {
              switch (option) {
                case "multipleChoice":
                  draft.generateMultipleChoiceQuiz = checked;
                  break;
                case "shortAnswer":
                  draft.generateShortAnswerQuiz = checked;
                  break;
                case "ankiFlashcards":
                  draft.generateAnkiFlashcards = checked;
                  break;
                case "presentationSlides":
                  draft.generatePresentationSlides = checked;
                  break;
                case "summary":
                  draft.generateSummary = checked;
                  break;
                case "mindmap":
                  draft.generateMindmap = checked;
                  break;
                case "readabilityAnalysis":
                  draft.generateReadabilityAnalysis = checked;
                  break;
                case "lessonPlan":
                  draft.generateLessonPlan = checked;
                  break;
              }
            }),
          );

          if (file) {
            const formData = new FormData();
            formData.append("file", file);

            // Get updated state after setting the new option
            const updatedState = get();
            const options = {
              generateMultipleChoiceQuiz: updatedState.generateMultipleChoiceQuiz,
              generateShortAnswerQuiz: updatedState.generateShortAnswerQuiz,
              generateAnkiFlashcards: updatedState.generateAnkiFlashcards,
              generatePresentationSlides: updatedState.generatePresentationSlides,
              generateSummary: updatedState.generateSummary,
              generateMindmap: updatedState.generateMindmap,
              generateReadabilityAnalysis: updatedState.generateReadabilityAnalysis,
              generateLessonPlan: updatedState.generateLessonPlan,
            };

            axios
              .post<DocumentInfo>("/api/get_document_info", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 300000,
              })
              .then((response) => {
                const updatedStateAfterResponse = get();
                const costBreakdown = calculateCostBreakdown(response.data.wordCount || 0, options);

                // Apply promo code discounts if applicable
                if (updatedStateAfterResponse.appliedPromoCode) {
                  const { appliedPromoCode } = updatedStateAfterResponse;

                  if (appliedPromoCode.type === "percentage") {
                    const discountMultiplier = 1 - appliedPromoCode.value / 100;
                    Object.keys(costBreakdown).forEach((key) => {
                      if (key !== "totalCost") {
                        costBreakdown[key as keyof CostBreakdown] *= discountMultiplier;
                      }
                    });
                    // Recalculate total after applying discount
                    costBreakdown.totalCost =
                      Object.values(costBreakdown).reduce((acc, val) => acc + val, 0) -
                      costBreakdown.quizDiscount;
                  } else if (appliedPromoCode.type === "fixed") {
                    costBreakdown.baseCost = Math.max(
                      0,
                      costBreakdown.baseCost - appliedPromoCode.value,
                    );
                    costBreakdown.totalCost =
                      Object.values(costBreakdown).reduce((acc, val) => acc + val, 0) -
                      costBreakdown.quizDiscount;
                  } else if (appliedPromoCode.type === "free_quizzes") {
                    costBreakdown.multipleChoiceCost = 0;
                    costBreakdown.shortAnswerCost = 0;
                    costBreakdown.quizDiscount = 0;
                    costBreakdown.totalCost = Object.values(costBreakdown).reduce(
                      (acc, val) => acc + val,
                      0,
                    );
                  }
                }

                set(
                  produce((draft: AppState) => {
                    draft.documentInfo = response.data;
                    draft.creditCost = costBreakdown.totalCost;
                    draft.costBreakdown = costBreakdown;
                    draft.showPreview = true;
                  }),
                );
              })
              .catch((error) => {
                console.error("Error updating costs after option change:", safeStringify(error));
              });
          }
        },

        handleSubmit: (
          submitFile: File,
          submitOptions: {
            generateMultipleChoiceQuiz: boolean;
            generateShortAnswerQuiz: boolean;
            generateAnkiFlashcards: boolean;
            generatePresentationSlides: boolean;
            generateSummary: boolean;
            generateMindmap: boolean;
            generateReadabilityAnalysis: boolean;
            generateLessonPlan: boolean;
            lessonPlanTargetAudience: string;
            presentationTheme: string;
          },
        ) => {
          const state = get();

          // Map the options to match the expected format
          const costOptions = {
            generateMultipleChoiceQuiz: submitOptions.generateMultipleChoiceQuiz,
            generateShortAnswerQuiz: submitOptions.generateShortAnswerQuiz,
            generateAnkiFlashcards: submitOptions.generateAnkiFlashcards,
            generatePresentationSlides: submitOptions.generatePresentationSlides,
            generateSummary: submitOptions.generateSummary,
            generateMindmap: submitOptions.generateMindmap,
            generateReadabilityAnalysis: submitOptions.generateReadabilityAnalysis,
            generateLessonPlan: submitOptions.generateLessonPlan,
          };

          const creditCost = calculateCostBreakdown(
            state.documentInfo?.wordCount || 0,
            costOptions,
          ).totalCost;

          set({
            documentFile: submitFile,
            ...submitOptions,
            showConfirmModal: true,
            currentJobId: null,
            jobStatus: null,
            creditCost,
          });
        },

        resetJobState: () => {
          set({
            currentJobId: null,
            jobStatus: null,
            showProgressTracker: false,
            showLogViewer: false,
            jobCompleted: false,
            documentFile: null,
            jobCompletionHandled: false,
            zipDownloaded: false,
            hasValidJobId: false,
            lastKnownStatus: null,
            lastUpdateTime: null,
            lastLogId: null,
            processedFiles: [],
            logs: {},
            error: null,
            isProcessingDocument: false,
            documentInfo: null, // Add this
            creditCost: null, // Add this
            costBreakdown: {
              baseCost: 0,
              multipleChoiceCost: 0,
              shortAnswerCost: 0,
              ankiFlashcardsCost: 0,
              presentationSlidesCost: 0,
              summaryCost: 0,
              mindmapCost: 0,
              readabilityAnalysisCost: 0,
              lessonPlanCost: 0,
              quizDiscount: 0,
              totalCost: 0,
            }, // Add this
            showPreview: false, // Add this
          });
        },

        confirmProcessing: async () => {
          const state = get();

          if (state.isProcessingDocument) {
            console.log("Already processing, returning");
            return;
          }

          // Add these state updates immediately
          set({
            showConfirmModal: false,
            isProcessingDocument: true,
            jobCompleted: false,
            jobCompletionHandled: false,
            resultsReady: false,
            zipDownloaded: false,
            hasValidJobId: true,
            error: null,
            scrollToResults: true,
            // Add this line to show progress tracker immediately
            showProgressTracker: true,
          });

          try {
            console.log("Starting document processing...");
            if (!state.documentFile) {
              throw new Error("No document file selected");
            }

            const formData = new FormData();
            formData.append("file", state.documentFile);

            // Build query parameters
            const searchParams = new URLSearchParams({
              generate_multiple_choice_quiz: state.generateMultipleChoiceQuiz ? "1" : "0",
              generate_short_answer_quiz: state.generateShortAnswerQuiz ? "1" : "0",
              generate_anki_flashcards: state.generateAnkiFlashcards ? "1" : "0",
              generate_presentation_slides: state.generatePresentationSlides ? "1" : "0",
              generate_summary: state.generateSummary ? "1" : "0",
              generate_mindmap: state.generateMindmap ? "1" : "0",
              generate_readability_analysis: state.generateReadabilityAnalysis ? "1" : "0",
              generate_lesson_plan: state.generateLessonPlan ? "1" : "0",
              lesson_plan_target_audience: state.lessonPlanTargetAudience || "",
              presentation_theme: state.presentationTheme || "black",
            });

            // Ensure we're using the absolute URL with the correct base
            const apiUrl = "/api/process_document";
            const response = await axios.post<ProcessDocumentApiResponse>(
              `${apiUrl}?${searchParams.toString()}`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                  if (progressEvent.total) {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    if (progress === 100) {
                      console.log("Upload complete, awaiting processing...");
                    }
                  }
                },
                // Add withCredentials to ensure cookies are sent
                withCredentials: true,
              },
            );

            console.log("Processing response:", safeStringify(response.data));

            if (response.data.job_id) {
              const updatedJobStatus = updateJobStatus(response.data.initial_status);

              set((prevState) => ({
                ...prevState,
                currentJobId: response.data.job_id,
                currentRequestId: response.data.request_id,
                jobStatus: updatedJobStatus,
                isProcessingDocument: true,
                error: null,
              }));

              get().fetchJobStatus();

              trackEvent("document_processing_started", {
                file_name: state.documentFile.name,
                file_size: state.documentFile.size,
                file_type: state.documentFile.type,
                multiple_choice_quiz: state.generateMultipleChoiceQuiz,
                short_answer_quiz: state.generateShortAnswerQuiz,
                anki_flashcards: state.generateAnkiFlashcards,
                presentation_slides: state.generatePresentationSlides,
                summary: state.generateSummary,
                mindmap: state.generateMindmap,
                readability_analysis: state.generateReadabilityAnalysis,
                lesson_plan: state.generateLessonPlan,
              });

              await get().refetchUserInfo();
            } else {
              throw new Error("No job ID received from server");
            }
          } catch (error) {
            console.error("Error processing document:", safeStringify(error));
            let errorMessage = "Failed to process document";

            if (axios.isAxiosError(error)) {
              // Log more detailed error information
              console.error(
                "Axios error details:",
                safeStringify({
                  response: error.response?.data,
                  status: error.response?.status,
                  headers: error.response?.headers,
                  config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    headers: error.config?.headers,
                  },
                }),
              );

              if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
              } else if (error.response?.data?.details) {
                errorMessage = error.response.data.details;
              } else if (error.message) {
                errorMessage = error.message;
              }

              trackEvent("document_processing_error", {
                error_type: "axios_error",
                error_message: errorMessage,
                status_code: error.response?.status ?? "unknown",
              });
            } else if (error instanceof Error) {
              errorMessage = error.message;

              trackEvent("document_processing_error", {
                error_type: "generic_error",
                error_message: errorMessage,
              });
            }

            set({
              error: errorMessage,
              currentJobId: null,
              currentRequestId: null,
              jobStatus: null,
              isProcessingDocument: false,
              isLoadingFiles: false,
              showProgressTracker: false,
              jobCompleted: false,
              jobCompletionHandled: false,
              resultsReady: false,
              zipDownloaded: false,
              hasValidJobId: false,
              processedFiles: [],
            });
            notify.error({ content: safeStringify(errorMessage) });
            await get().refetchUserInfo();
          }
        },

        handleJobComplete: async (job_id: string) => {
          const state = get();
          if (state.jobCompletionHandled || state.isDownloading) {
            return;
          }

          set({
            jobCompletionHandled: true,
            jobCompleted: true,
            currentJobId: job_id,
            isCheckingStatus: false,
            isProcessingDocument: false,
          });

          await get().fetchProcessedFiles(job_id);

          // Only set resultsReady to true if we have processed files
          set((state) => ({
            resultsReady: state.processedFiles.length > 0,
          }));

          // CHANGE HERE: Only attempt download if results are actually ready
          if (get().resultsReady && get().processedFiles.length > 0) {
            notify.success({ content: "Job completed successfully!" });

            // Add slight delay before starting download
            await new Promise((resolve) => setTimeout(resolve, 1000));

            set({ isDownloading: true });
            try {
              await handleDownload(job_id);
              set({ zipDownloaded: true });
            } catch (error) {
              console.error("Error downloading results:", safeStringify(error));
              get().setError("Failed to download results");
            } finally {
              set({ isDownloading: false });
            }
          }
        },

        refetchUserInfo: async () => {
          set({ isLoadingUserInfo: true });
          try {
            const response = await axios.get<UserInfo>("/api/user/info");
            set({ userInfo: response.data, isLoadingUserInfo: false });
          } catch (error) {
            console.error("Error fetching user info:", safeStringify(error));
            if (axios.isAxiosError(error)) {
              console.error("Error details:", safeStringify(error.response?.data));
            }
            set({ isLoadingUserInfo: false });
            get().setError("Failed to fetch user info");
          }
        },

        applyPromoCodeWrapper: async (code: string) => {
          set({ isApplyingPromoCode: true });
          try {
            const response = await apiApplyPromoCode(code);
            if (response.success) {
              if (response.type !== "free_credits") {
                set((state) => ({
                  ...state,
                  promoCode: code,
                  appliedPromoCode: response,
                }));
              }
              notify.success({ content: `Promo code applied! ${response.description}` });
              get().refetchUserInfo();
            }
            return response;
          } catch (error) {
            console.error("Error applying promo code:", safeStringify(error));
            notify.error({ content: "Failed to apply promo code" });
            return null;
          } finally {
            set({ isApplyingPromoCode: false });
          }
        },

        recalculateCosts: async (
          file: File,
          options: {
            generateMultipleChoiceQuiz: boolean;
            generateShortAnswerQuiz: boolean;
            generateAnkiFlashcards: boolean;
            generatePresentationSlides: boolean;
            generateSummary: boolean;
            generateMindmap: boolean;
            generateReadabilityAnalysis: boolean;
            generateLessonPlan: boolean;
          },
        ) => {
          const state = get();
          if (!file) {
            console.error("No file provided for recalculating costs");
            return;
          }
          try {
            const result = await calculateCreditCost(file);
            const costBreakdown = calculateCostBreakdown(result.documentInfo.wordCount || 0, {
              generateMultipleChoiceQuiz: options.generateMultipleChoiceQuiz,
              generateShortAnswerQuiz: options.generateShortAnswerQuiz,
              generateAnkiFlashcards: options.generateAnkiFlashcards,
              generatePresentationSlides: options.generatePresentationSlides,
              generateSummary: options.generateSummary,
              generateMindmap: options.generateMindmap,
              generateReadabilityAnalysis: options.generateReadabilityAnalysis,
              generateLessonPlan: options.generateLessonPlan,
            });

            if (state.appliedPromoCode) {
              const { appliedPromoCode } = state;

              if (appliedPromoCode.type === "percentage") {
                const discountMultiplier = 1 - appliedPromoCode.value / 100;
                Object.keys(costBreakdown).forEach((key) => {
                  if (key !== "totalCost") {
                    costBreakdown[key as keyof CostBreakdown] *= discountMultiplier;
                  }
                });
                costBreakdown.totalCost =
                  Object.values(costBreakdown).reduce((acc, val) => acc + val, 0) -
                  costBreakdown.quizDiscount;
              } else if (appliedPromoCode.type === "fixed") {
                costBreakdown.baseCost = Math.max(
                  0,
                  costBreakdown.baseCost - appliedPromoCode.value,
                );
                costBreakdown.totalCost =
                  Object.values(costBreakdown).reduce((acc, val) => acc + val, 0) -
                  costBreakdown.quizDiscount;
              } else if (appliedPromoCode.type === "free_quizzes") {
                costBreakdown.multipleChoiceCost = 0;
                costBreakdown.shortAnswerCost = 0;
                costBreakdown.quizDiscount = 0;
                costBreakdown.totalCost = Object.values(costBreakdown).reduce(
                  (acc, val) => acc + val,
                  0,
                );
              }
            }

            set({
              creditCost: costBreakdown.totalCost,
              costBreakdown: costBreakdown,
            });
          } catch (error) {
            console.error("Error recalculating costs:", safeStringify(error));
            get().setError("Failed to recalculate credit cost");
          }
        },

        handlePreview: () => set({ showPreview: true }),
        fetchProcessedFiles: async (job_id: string) => {
          try {
            console.log(`Fetching processed files for job: ${job_id}`);
            set({ isLoadingFiles: true, error: null });

            let response: AxiosResponse<FileData[]>;

            try {
              response = await axios.get<FileData[]>(
                `/api/get_processed_files/${job_id}?checkLatest=true`,
                {
                  headers: {
                    "Cache-Control": "no-cache",
                    Pragma: "no-cache",
                  },
                },
              );
            } catch (error: any) {
              if (axios.isAxiosError(error) && error.response?.status === 304) {
                console.log(`No changes in processed files for job: ${job_id}`);
                set({
                  isLoadingFiles: false,
                  error: null,
                  resultsReady: true,
                });
                return;
              } else {
                throw error;
              }
            }

            console.log(`Received ${response.data.length} files`);

            const validFiles = response.data
              .filter((file) => {
                try {
                  if (file.filename.startsWith("_Package_of_Results_for_Document__")) {
                    console.log(`Skipping file: ${file.filename} (excluded from validation)`);
                    return true;
                  }

                  const validExtensions = [
                    ".md",
                    ".html",
                    ".pdf",
                    ".txt",
                    ".csv",
                    ".json",
                    ".apkg",
                  ];
                  const isValidType = validExtensions.some((ext) =>
                    file.filename.toLowerCase().endsWith(ext),
                  );

                  if (!isValidType) {
                    console.warn(`Skipping unsupported file type: ${file.filename}`);
                    return false;
                  }

                  if (!file.filename.endsWith(".pdf") && !file.filename.endsWith(".apkg")) {
                    try {
                      const decoded = atob(file.content);
                      const bytes = new Uint8Array(decoded.length);
                      for (let i = 0; i < decoded.length; i++) {
                        bytes[i] = decoded.charCodeAt(i);
                      }
                      new TextDecoder().decode(bytes);
                    } catch (error) {
                      console.error(
                        `Content decoding failed for ${file.filename}:`,
                        safeStringify(error),
                      );
                      return false;
                    }
                  }

                  return true;
                } catch (error) {
                  console.error(
                    `File validation failed for ${file.filename}:`,
                    safeStringify(error),
                  );
                  return false;
                }
              })
              .sort((a, b) => {
                const fileOrder: Record<string, number> = {
                  final_optimized_document: 1,
                  optimized_document: 2,
                  multiple_choice_quiz: 3,
                  short_answer_quiz: 4,
                  anki_flashcards: 5,
                  presentation_slides: 6,
                  summary: 7,
                  mindmap: 8,
                  readability_analysis: 9,
                  lesson_plan: 10,
                };

                const getFileOrder = (filename: string): number => {
                  for (const [key, order] of Object.entries(fileOrder)) {
                    if (filename.includes(key)) return order;
                  }
                  return 999;
                };

                return getFileOrder(a.filename) - getFileOrder(b.filename);
              });

            console.log(`${validFiles.length} valid files after filtering and sorting`);

            if (validFiles.length === 0) {
              console.warn("No valid files found after filtering");
              set({
                processedFiles: [],
                isLoadingFiles: false,
                resultsReady: false,
                error: "No valid files found in the response",
              });
              return;
            }

            set({
              processedFiles: validFiles,
              isLoadingFiles: false,
              resultsReady: true,
              error: null,
            });

            if (validFiles.length < response.data.length) {
              console.warn(
                `${response.data.length - validFiles.length} files were filtered out due to validation`,
              );
            }
          } catch (error) {
            console.error("Error fetching processed files:", safeStringify(error));
            set({
              error: error instanceof Error ? error.message : "Failed to fetch processed files",
              isLoadingFiles: false,
              resultsReady: false,
              processedFiles: [],
            });
          }
        },

        updateJobStatus: (status: FastAPIJobStatusUpdate) =>
          set((state) => {
            const updatedJobStatus: JobStatus = {
              id: state.jobStatus?.id || "",
              job_id: status.job_id,
              request_id: status.job_id, // Use job_id as request_id
              status: status.status,
              progress_percentage: status.progress_percentage,
              status_description: status.status_description ?? null,
              last_processed: null, // This field is not in FastAPIJobStatusUpdate
              last_optimized: null, // This field is not in FastAPIJobStatusUpdate
              optimization_quality_score: status.optimization_quality_score,
              total_input_tokens: status.total_input_tokens,
              total_output_tokens: status.total_output_tokens,
              error_message: status.error_message,
              created_at: state.jobStatus?.created_at || new Date(),
              updated_at: new Date(status.update_time),
              last_polled: new Date(),
            };

            return {
              jobStatus: updatedJobStatus,
              lastKnownStatus: status.status,
              lastUpdateTime: status.update_time,
              statusCheckAttempts: 0,
            };
          }),

        addLogEntries: (job_id: string, newLogs: LogEvent[]) => {
          set((state) => ({
            logs: {
              ...state.logs,
              [job_id]: [...(state.logs[job_id] || []), ...newLogs],
            },
            lastLogId: newLogs.length > 0 ? newLogs[newLogs.length - 1].id : state.lastLogId,
          }));
        },

        fetchJobStatus: async () => {
          const {
            currentJobId,
            lastKnownStatus,
            lastUpdateTime,
            jobCompleted,
            lastStatusCheck,
            statusCheckAttempts,
          } = get();
          if (!currentJobId || jobCompleted) return;

          const now = Date.now();
          const timeSinceLastCheck = now - lastStatusCheck;
          const backoffTime = Math.min(30000, 2 ** statusCheckAttempts * 1000); // Max 30 seconds

          if (timeSinceLastCheck < backoffTime) return;

          set({ lastStatusCheck: now });
          try {
            const response = await axios.get<FastAPIJobStatusUpdate>(
              `/api/job_status/${currentJobId}`,
              {
                params: {
                  last_known_status: lastKnownStatus ?? undefined,
                  last_update_time: lastUpdateTime ?? undefined,
                },
              },
            );

            // Successful request: clear any error backoff.
            set({ statusCheckAttempts: 0 });

            if (response.data.changed) {
              set((state) => ({
                jobStatus: {
                  ...state.jobStatus,
                  id: state.jobStatus?.id || "",
                  job_id: response.data.job_id,
                  request_id: response.data.job_id,
                  status: response.data.status,
                  progress_percentage: response.data.progress_percentage,
                  status_description: response.data.status_description ?? null,
                  last_processed: null, // This field is not in FastAPIJobStatusUpdate
                  last_optimized: null, // This field is not in FastAPIJobStatusUpdate
                  optimization_quality_score: response.data.optimization_quality_score,
                  total_input_tokens: response.data.total_input_tokens,
                  total_output_tokens: response.data.total_output_tokens,
                  error_message: response.data.error_message,
                  created_at: state.jobStatus?.created_at || new Date(),
                  updated_at: new Date(response.data.update_time),
                  last_polled: new Date(),
                },
                lastKnownStatus: response.data.status,
                lastUpdateTime: response.data.update_time,
                statusCheckAttempts: 0,
              }));
              if (response.data.status === "completed") {
                set({ jobCompleted: true });
                get().handleJobComplete(currentJobId);
              }
            }
          } catch (error) {
            console.error("Error fetching job status:", safeStringify(error));
            // Backoff on consecutive errors only.
            set((state) => ({ statusCheckAttempts: state.statusCheckAttempts + 1 }));
          }
        },

        forceRefreshJobStatus: async () => {
          set({ statusCheckAttempts: 0, lastStatusCheck: 0 });
          await get().fetchJobStatus();
        },

        fetchLogs: async (requestId: string, lastLogId: number | null) => {
          try {
            const response = await axios.get<LogEvent[]>("/api/logs", {
              params: { requestId, lastLogId },
            });

            if (response.data.length > 0) {
              set((state) => ({
                logs: {
                  ...state.logs,
                  [requestId]: [...(state.logs[requestId] || []), ...response.data],
                },
                // Add a timestamp to force a state update
                lastLogUpdate: Date.now(),
              }));
              return response.data[response.data.length - 1].id;
            }
            return lastLogId;
          } catch (error) {
            console.error("Error fetching logs:", safeStringify(error));
            set({ error: "Failed to fetch logs" });
            return lastLogId;
          }
        },

        hasProgressInFlags: (
          oldFlags: { completed: Record<string, boolean> } | null,
          newFlags: { completed: Record<string, boolean> },
        ) => {
          if (!oldFlags) return true;
          const oldCompleted = Object.values(oldFlags.completed).filter(
            (val) => val === true,
          ).length;
          const newCompleted = Object.values(newFlags.completed).filter(
            (val) => val === true,
          ).length;
          return newCompleted > oldCompleted;
        },

        additionalGeneration: {
          isGeneratingAdditional: false,
          additionalGenerationJobId: null,
          additionalGenerationProgress: 0,
          additionalGenerationStatus: "",
          additionalGenerationError: null,
          existingDocumentTypes: [],
          lastKnownGenerationFlags: null,
          stages: [],
          statusTransitions: [],
          optimization_qualityScore: null,
          inputTokens: null,
          outputTokens: null,
          lastProcessed: null,
          lastOptimized: null,
          statusDescription: "",
          hasSeenProcessingState: false,
          processingStartTime: null,
          showResults: false,
          keepModalOpen: false,
        },

        startAdditionalGeneration: async (jobId: string, options: AdditionalGenerationOptions) => {
          try {
            // Log the request we're about to make
            console.log("Making additional generation request:", {
              endpoint: "/api/additional_documents",
              payload: {
                jobId,
                options,
              },
            });

            const response = await axios.post("/api/additional_documents", {
              jobId,
              options,
            });

            if (!response.data?.job_id) {
              throw new Error("Invalid response from server");
            }

            set((state) => ({
              additionalGeneration: {
                ...state.additionalGeneration,
                additionalGenerationJobId: response.data.job_id,
              },
            }));

            return response.data;
          } catch (error) {
            if (axios.isAxiosError(error)) {
              console.error("Network error details:", {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
                config: error.config,
              });
            }
            throw error;
          }
        },

        pollAdditionalGenerationStatus: async () => {
          const state = get().additionalGeneration;

          if (!state.isGeneratingAdditional || !state.additionalGenerationJobId) {
            return;
          }

          try {
            const response = await axios.get<FastApiJobSummaryResponse>(
              `/api/document_job_summary/${state.additionalGenerationJobId}`,
            );

            if (response.data.changed) {
              const currentStatus = response.data.status;
              const previousStatus = state.additionalGenerationStatus;

              if (get().normalizeStatus(currentStatus) !== get().normalizeStatus(previousStatus)) {
                const statusTransition: StatusTransition = {
                  fromStatus: previousStatus,
                  toStatus: currentStatus,
                  timestamp: Date.now(),
                };

                set((state) => ({
                  additionalGeneration: {
                    ...state.additionalGeneration,
                    statusTransitions: [
                      ...state.additionalGeneration.statusTransitions,
                      statusTransition,
                    ],
                  },
                }));
              }

              // Only set hasSeenProcessingState if we see a NEW processing state after generation starts
              if (
                (currentStatus.includes("processing") ||
                  currentStatus.includes("generating") ||
                  currentStatus.includes("starting")) &&
                state.processingStartTime && // Ensure generation has started
                Date.now() - state.processingStartTime > 1000
              ) {
                // Ensure we're not seeing old status
                set((state) => ({
                  additionalGeneration: {
                    ...state.additionalGeneration,
                    hasSeenProcessingState: true,
                  },
                }));
              }

              const generationFlags = response.data.generation_flags;
              // Compare only newly requested types for progress
              const hasNewProgress = Object.entries(generationFlags.requested)
                .filter(([key, requested]) => {
                  const isNewRequest = !state.lastKnownGenerationFlags?.requested?.[key];
                  return requested && isNewRequest;
                })
                .some(([key]) => generationFlags.completed?.[key]);

              if (hasNewProgress || currentStatus !== previousStatus) {
                const updatedStages = state.stages.map((stage) => ({
                  ...stage,
                  isCompleted:
                    stage.status === currentStatus ||
                    state.statusTransitions.some((t) => t.fromStatus === stage.status),
                }));

                const updates = {
                  additionalGenerationStatus: currentStatus,
                  additionalGenerationProgress: response.data.progress_percentage,
                  lastKnownGenerationFlags: generationFlags,
                  optimization_qualityScore: response.data.optimization_quality_score || null,
                  inputTokens: response.data.total_input_tokens || null,
                  outputTokens: response.data.total_output_tokens || null,
                  lastProcessed: response.data.last_processed || null,
                  lastOptimized: response.data.last_optimized || null,
                  stages: updatedStages,
                  statusDescription: get().generateStatusDescription(
                    currentStatus,
                    response.data.progress_percentage,
                  ),
                };

                // Check completion only for newly requested types
                const allRequestedTypesCompleted = Object.entries(generationFlags.requested)
                  .filter(([key, requested]) => {
                    const isNewRequest = !state.lastKnownGenerationFlags?.requested?.[key];
                    return requested && isNewRequest;
                  })
                  .every(([key]) => generationFlags.completed[key]);

                const timeSinceStart = state.processingStartTime
                  ? Date.now() - state.processingStartTime
                  : 0;
                const hasWaitedLongEnough = timeSinceStart > 10000;
                const isCompleted =
                  hasWaitedLongEnough &&
                  state.hasSeenProcessingState &&
                  currentStatus === "completed" &&
                  allRequestedTypesCompleted &&
                  hasNewProgress;

                set((state) => ({
                  additionalGeneration: {
                    ...state.additionalGeneration,
                    ...updates,
                    showResults: isCompleted ? true : state.additionalGeneration.showResults,
                    keepModalOpen: isCompleted ? true : state.additionalGeneration.keepModalOpen,
                    isGeneratingAdditional: isCompleted
                      ? false
                      : state.additionalGeneration.isGeneratingAdditional,
                    additionalGenerationProgress: isCompleted
                      ? 100
                      : response.data.progress_percentage,
                  },
                }));

                if (isCompleted) {
                  await get().handleAdditionalGenerationComplete(state.additionalGenerationJobId);
                  return;
                }
              }
            }

            if (state.isGeneratingAdditional) {
              setTimeout(() => get().pollAdditionalGenerationStatus(), 2000);
            }
          } catch (error) {
            get().handlePollingError(error);
          }
        },

        handleAdditionalGenerationComplete: async (jobId: string) => {
          try {
            await get().fetchProcessedFiles(jobId);

            set((state) => ({
              ...state,
              additionalGeneration: {
                ...state.additionalGeneration,
                isGeneratingAdditional: false,
                additionalGenerationStatus: "completed",
                additionalGenerationProgress: 100,
                statusDescription: "Generation completed successfully",
                stages: state.additionalGeneration.stages.map((stage: StageDefinition) => ({
                  ...stage,
                  isCompleted: true,
                })),
                showResults: true,
                keepModalOpen: true,
              },
            }));

            notify.success({ content: "Additional documents generated successfully" });
            await get().refetchUserInfo();
          } catch (error) {
            get().handlePollingError(error);
          }
        },

        updateGenerationStages: (options) => {
          const stages: StageDefinition[] = [
            {
              title: "Initializing",
              status: "starting",
              iconType: "build",
              isCompleted: false,
            },
          ];

          Object.entries(options).forEach(([key, value]) => {
            if (
              value &&
              !get().additionalGeneration.existingDocumentTypes.includes(key as DocumentOptionKey)
            ) {
              const componentName = (key as DocumentOptionKey)
                .replace(/^generate/, "")
                .replace(/([A-Z])/g, "_$1")
                .toLowerCase()
                .replace(/^_/, "");

              stages.push({
                title: `Generating ${key.replace("generate", "")}`,
                status: `generating_${componentName}`,
                iconType: "orderedList",
                isCompleted: false,
              });
            }
          });

          stages.push(
            {
              title: "Packaging Results",
              status: "packaging_results",
              iconType: "nodeExpand",
              isCompleted: false,
            },
            {
              title: "Completed",
              status: "completed",
              iconType: "checkCircle",
              isCompleted: false,
            },
          );

          set((state) => ({
            additionalGeneration: {
              ...state.additionalGeneration,
              stages,
            },
          }));
        },
        logStatusTransition: (fromStatus: string, toStatus: string) => {
          // Skip logging if statuses are the same after normalization
          if (
            fromStatus === toStatus ||
            get().normalizeStatus(fromStatus) === get().normalizeStatus(toStatus)
          ) {
            return;
          }

          // Get current state snapshot
          const currentState = get().additionalGeneration;
          const timestamp = Date.now();

          console.log(`Status Transition [${new Date(timestamp).toISOString()}]:`, {
            from: fromStatus,
            to: toStatus,
            jobId: currentState.additionalGenerationJobId,
            hasSeenProcessingState: currentState.hasSeenProcessingState,
            processingStartTime: currentState.processingStartTime,
            timeSinceStart: currentState.processingStartTime
              ? timestamp - currentState.processingStartTime
              : null,
            currentFlags: currentState.lastKnownGenerationFlags,
          });

          const newTransition: StatusTransition = {
            fromStatus,
            toStatus,
            timestamp,
          };

          set((state) => ({
            additionalGeneration: {
              ...state.additionalGeneration,
              statusTransitions: [...state.additionalGeneration.statusTransitions, newTransition],
              // Update hasSeenProcessingState if transitioning to a processing state
              hasSeenProcessingState:
                state.additionalGeneration.hasSeenProcessingState ||
                toStatus.includes("processing") ||
                toStatus.includes("generating") ||
                toStatus.includes("starting"),
            },
          }));
        },

        updateGenerationFlags: (flags: {
          requested: Record<string, boolean>;
          completed: Record<string, boolean>;
        }) => {
          // Defensive check for valid flags object
          if (
            !flags ||
            typeof flags !== "object" ||
            !flags.requested ||
            !flags.completed ||
            typeof flags.requested !== "object" ||
            typeof flags.completed !== "object"
          ) {
            console.error("Invalid flags provided to updateGenerationFlags:", flags);
            return;
          }

          const previousFlags = get().additionalGeneration.lastKnownGenerationFlags;

          console.log("Updating generation flags:", {
            previous: previousFlags,
            new: flags,
            changes: {
              requested: Object.entries(flags.requested)
                .filter(([key, val]) => previousFlags?.requested?.[key] !== val)
                .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {}),
              completed: Object.entries(flags.completed)
                .filter(([key, val]) => previousFlags?.completed?.[key] !== val)
                .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {}),
            },
          });

          set((state) => ({
            additionalGeneration: {
              ...state.additionalGeneration,
              lastKnownGenerationFlags: flags,
            },
          }));
        },

        normalizeStatus: (status: string) => {
          if (!status) return "";

          // Handle special case for completed status during additional generation
          if (status === "completed") {
            const state = get().additionalGeneration;
            const hasIncompleteGenerations =
              state.lastKnownGenerationFlags?.requested &&
              Object.entries(state.lastKnownGenerationFlags?.requested).some(
                ([key, requested]) =>
                  requested && !state.lastKnownGenerationFlags?.completed?.[key],
              );

            if (hasIncompleteGenerations) {
              return "processing_additional";
            }
          }

          return status.replace(/_+$/, "");
        },

        generateStatusDescription: (status: string, progress: number) => {
          if (!status) return "Unknown status";

          const normalizedStatus = get().normalizeStatus(status);

          const componentName = normalizedStatus
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          // Add more context based on status
          let contextSuffix = "";
          if (progress === 100 && normalizedStatus === "completed") {
            contextSuffix = " - All requested documents generated";
          } else if (normalizedStatus === "processing_additional") {
            contextSuffix = " - Generating additional documents";
          }

          return `${componentName} - ${progress}% complete${contextSuffix}`;
        },

        handlePollingError: (error: unknown) => {
          console.error("Error in additional generation:", safeStringify(error));

          // Get current state for context
          const currentState = get().additionalGeneration;

          let errorMessage: string;
          let errorDetails: any = {};

          if (axios.isAxiosError(error)) {
            errorMessage = error.response?.data?.error || error.message;
            errorDetails = {
              status: error.response?.status,
              data: error.response?.data,
              config: {
                url: error.config?.url,
                method: error.config?.method,
              },
            };
          } else if (error instanceof Error) {
            errorMessage = error.message;
            errorDetails = { stack: error.stack };
          } else {
            errorMessage = "An unexpected error occurred";
            errorDetails = { error };
          }

          // Log detailed error context
          console.error(
            "Additional generation error context:",
            safeStringify({
              errorMessage,
              errorDetails,
              jobId: currentState.additionalGenerationJobId,
              currentStatus: currentState.additionalGenerationStatus,
              progress: currentState.additionalGenerationProgress,
              flags: currentState.lastKnownGenerationFlags,
            }),
          );

          set((state) => ({
            additionalGeneration: {
              ...state.additionalGeneration,
              isGeneratingAdditional: false,
              additionalGenerationError: errorMessage,
              additionalGenerationStatus: "failed",
              statusDescription: `Generation failed: ${errorMessage}`,
            },
          }));
        },
        forceReset: () => {
          // Clear all localStorage
          window.localStorage.clear();

          // Force reload the page
          window.location.reload();
        },
      }),
      {
        name: "app-storage",
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name);
            return str ? JSON.parse(str) : null;
          },
          setItem: (name, value) => {
            if (__persistDisabled || !__canWriteStorage()) {
              __persistDisabled = true;
              return;
            }
            const serializedValue = JSON.stringify(value, (key, val) => {
              if (val instanceof Date) {
                return val.toISOString();
              }
              return val;
            });
            try {
              localStorage.setItem(name, serializedValue);
            } catch (e) {
              // Avoid crashing the app if storage is full; log once in dev
              if (process.env.NODE_ENV !== "production") {
                console.warn("Persist storage skipped (quota exceeded or blocked):", e);
              }
              __persistDisabled = true;
            }
          },
          removeItem: (name) => {
            localStorage.removeItem(name);
          },
        },

        // Persist only a small, safe subset to avoid exceeding localStorage quota
        partialize: (state) =>
          ({
            // UI preferences and options
            generateMultipleChoiceQuiz: state.generateMultipleChoiceQuiz,
            generateShortAnswerQuiz: state.generateShortAnswerQuiz,
            generateAnkiFlashcards: state.generateAnkiFlashcards,
            generatePresentationSlides: state.generatePresentationSlides,
            generateSummary: state.generateSummary,
            generateMindmap: state.generateMindmap,
            generateReadabilityAnalysis: state.generateReadabilityAnalysis,
            generateLessonPlan: state.generateLessonPlan,
            lessonPlanTargetAudience: state.lessonPlanTargetAudience,
            presentationTheme: state.presentationTheme,
            showPastJobs: state.showPastJobs,
            // Promo code state is small and useful across sessions
            promoCode: state.promoCode,
            appliedPromoCode: state.appliedPromoCode,
          }) as any,

        // Add this merge function to handle rehydration
        merge: (persistedState: any, currentState) => {
          return {
            ...currentState,
            ...persistedState,
            additionalGeneration: {
              ...currentState.additionalGeneration,
              ...(persistedState.additionalGeneration || {}),
              isGeneratingAdditional: false, // Reset to false
              existingDocumentTypes: Array.isArray(
                persistedState.additionalGeneration?.existingDocumentTypes,
              )
                ? persistedState.additionalGeneration.existingDocumentTypes
                : [],
            },
          };
        },
      },
    ),
  ),
);

export default useAppStore;
